"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediasoupSource = void 0;
const log_node_1 = require("@video/log-node");
const mobx_1 = require("mobx");
const api_1 = require("../api");
const adapter_1 = require("../api/adapter");
const errors_1 = require("./errors");
const helper_1 = require("./player/helper");
const event_emitter_1 = require("./utils/events/event-emitter");
const defaultMediasoupSourceOptions = {
    replaceTracks: true,
};
class MediasoupSource extends event_emitter_1.ObservableEventEmitter {
    /**
     * @throws {ErrorCode.MediaStreamNotSupported} if MediaStream is not supported by device
     */
    constructor(ctx, peer, streamName, options = defaultMediasoupSourceOptions) {
        var _a;
        super();
        this.consumers = {};
        this.currentQuality = null;
        this.availableQualities = [];
        this.ctx = ctx;
        (0, mobx_1.makeObservable)(this, {
            currentQuality: mobx_1.observable.ref,
            availableQualities: mobx_1.observable.shallow,
        });
        if (!adapter_1.device.isImplements(adapter_1.Feature.MEDIA_STREAM)) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.MediaStreamNotSupported, "MediaStream is not supported by device", {}));
        }
        this.options = options;
        this.peer = peer;
        this.streamName = streamName;
        this.stream = new adapter_1.device.MediaStream();
        (_a = this.peer.call) === null || _a === void 0 ? void 0 : _a.on("accessDenied", this.handlePeerAccessDenied);
        this.addInnerDisposer(() => {
            var _a;
            (_a = this.peer.call) === null || _a === void 0 ? void 0 : _a.removeListener("accessDenied", this.handlePeerAccessDenied);
        });
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()");
    }
    handlePeerAccessDenied(ev) {
        this.emit("accessDenied", ev);
    }
    get source() {
        return this.stream;
    }
    get consumerIds() {
        return Object.keys(this.consumers);
    }
    addConsumer(consumer, emitSource = true) {
        if (!consumer.dontAdd) {
            if (consumer.track.kind === "video" && this.stream.getVideoTracks().length > 0) {
                this.stream.getVideoTracks().forEach((t) => this.stream.removeTrack(t));
            }
            if (consumer.track.kind === "audio" && this.stream.getAudioTracks().length > 0) {
                this.stream.getAudioTracks().forEach((t) => this.stream.removeTrack(t));
            }
            if (consumer.track.kind === "video") {
                const qualities = (0, helper_1.fetchManifestQualities)(consumer.layers, null);
                (0, mobx_1.runInAction)(() => {
                    this.availableQualities = qualities;
                    const activeLayer = consumer.activeLayer;
                    // if not null then we have to update current quality
                    if (activeLayer != null) {
                        const qty = this.availableQualities.find((q) => { var _a; return ((_a = q.layer) === null || _a === void 0 ? void 0 : _a.id) === activeLayer.id; });
                        if (qty != null) {
                            this.currentQuality = qty;
                        }
                        else {
                            this.ctx.logger.warn("Consumer refers to a layer which doesn't exist", {
                                layers: consumer.layers,
                                activeLayer: consumer.activeLayer,
                            });
                        }
                    }
                });
            }
            this.stream.addTrack(consumer.track);
        }
        this.consumers[consumer.id] = consumer;
        if (emitSource && !consumer.dontAdd) {
            this.emit("source", this.stream);
        }
    }
    removeConsumer(consumerId, emitSource = true) {
        const consumer = this.consumers[consumerId];
        if (consumer != null) {
            this.stream.removeTrack(consumer.track);
            delete this.consumers[consumerId];
            if (this.stream.getTracks().length > 0) {
                if (emitSource) {
                    this.emit("source", this.stream);
                }
            }
        }
    }
    swapConsumers(add, remove) {
        remove.forEach((track) => {
            this.stream.removeTrack(track);
        });
        add.forEach((track) => {
            this.stream.addTrack(track);
        });
        this.emit("source", this.stream);
    }
    pauseConsumer(consumerId) {
        const consumer = this.consumers[consumerId];
        if (consumer == null) {
            return;
        }
        consumer.paused = true;
        if (this.stream.getTracks().some((t) => t.id === consumer.track.id)) {
            if (consumer.kind === "video") {
                this.emit("videoPaused", consumer.paused);
            }
            else {
                this.emit("audioMuted", consumer.paused);
            }
        }
    }
    resumeConsumer(consumerId) {
        const consumer = this.consumers[consumerId];
        if (consumer == null) {
            return;
        }
        consumer.paused = false;
        if (this.stream.getTracks().some((t) => t.id === consumer.track.id)) {
            if (consumer.kind === "video") {
                this.emit("videoPaused", consumer.paused);
            }
            else {
                this.emit("audioMuted", consumer.paused);
            }
        }
    }
    consumerLayersChanged(layer) {
        const qty = this.availableQualities.find((q) => { var _a; return ((_a = q.layer) === null || _a === void 0 ? void 0 : _a.id) === layer.id; });
        if (qty != null) {
            this.currentQuality = qty;
        }
    }
    consumerSourcesChanged(layers) {
        const qualities = (0, helper_1.fetchManifestQualities)(layers, null);
        this.availableQualities = qualities;
    }
    getTracks() {
        return this.stream.getTracks();
    }
    get hasAudio() {
        return Object.values(this.consumers).some((c) => c.kind === "audio" && c.dontAdd === false);
    }
    get hasVideo() {
        return Object.values(this.consumers).some((c) => c.kind === "video" && c.dontAdd === false);
    }
    get audioMuted() {
        const consumers = Object.values(this.consumers).filter((c) => c.kind === "audio" && c.dontAdd === false);
        if (consumers.length === 0) {
            return true;
        }
        return consumers.some((c) => c.paused);
    }
    get videoPaused() {
        const consumers = Object.values(this.consumers).filter((c) => c.kind === "video" && c.dontAdd === false);
        if (consumers.length === 0) {
            return true;
        }
        return consumers.some((c) => c.paused);
    }
    toJSON() {
        return {
            hasAudio: this.hasAudio,
            hasVideo: this.hasVideo,
            audioMuted: this.audioMuted,
            videoPaused: this.videoPaused,
            options: this.options,
            aggregates: {
                ...(0, log_node_1.extractAggregates)(this.peer, "support"),
                support: this.ctx.support.hash,
                streamName: this.streamName,
            },
        };
    }
}
exports.MediasoupSource = MediasoupSource;
MediasoupSource.displayName = "MediasoupSource";
//# sourceMappingURL=mediasoup-source.js.map